/** Desktop popover and mobile inline disclosure for advanced screener filters. */

import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import type { ScreenerFilterState } from "../../lib/types";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { countActiveAdvancedFilters, describeSecurityFilter } from "./active-advanced-filters";
import { AdvancedFilterControls } from "./advanced-filter-controls";

interface AdvancedFiltersDisclosureProps {
  filters: ScreenerFilterState;
  exchangeOptions: string[];
  presentation: "popover" | "inline";
  onFilterPatch: (filters: Partial<ScreenerFilterState>) => void;
}

function DisclosureLabel({ count }: { count: number }) {
  return (
    <>
      <span>Advanced filters</span>
      {count > 0 ? (
        <span aria-hidden="true" className="text-muted-foreground">
          {count} active
        </span>
      ) : null}
    </>
  );
}

/** Progressively reveals advanced controls while preserving their controlled state. */
export function AdvancedFiltersDisclosure({
  filters,
  exchangeOptions,
  presentation,
  onFilterPatch
}: AdvancedFiltersDisclosureProps) {
  const [inlineOpen, setInlineOpen] = useState(false);
  const contentId = useId();
  const statusId = useId();
  const activeCount = countActiveAdvancedFilters(filters);
  const securitySummary = describeSecurityFilter(filters);
  const activeLabel = `${activeCount} advanced ${activeCount === 1 ? "filter" : "filters"} active`;

  const triggerContents = (
    <>
      <span className="flex flex-1 items-center justify-between gap-2">
        <DisclosureLabel count={activeCount} />
      </span>
      <ChevronDown
        aria-hidden="true"
        className={cn(
          "h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180",
          inlineOpen && "rotate-180"
        )}
      />
    </>
  );

  return (
    <div className="space-y-2 border-t border-border pt-3">
      {securitySummary ? (
        <p className="text-xs text-muted-foreground">
          Security: <span className="font-medium text-foreground">{securitySummary}</span>
        </p>
      ) : null}
      <span id={statusId} className="sr-only">
        {activeLabel}
      </span>
      {presentation === "inline" ? (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="group w-full justify-between gap-2"
            aria-label="Advanced filters"
            aria-describedby={statusId}
            aria-expanded={inlineOpen}
            aria-controls={contentId}
            onClick={() => setInlineOpen((current) => !current)}
          >
            {triggerContents}
          </Button>
          {inlineOpen ? (
            <section id={contentId} aria-label="Advanced filters" className="space-y-3 pt-1">
              <AdvancedFilterControls
                filters={filters}
                exchangeOptions={exchangeOptions}
                onFilterPatch={onFilterPatch}
              />
            </section>
          ) : null}
        </>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="group w-full justify-between gap-2"
              aria-label="Advanced filters"
              aria-describedby={statusId}
            >
              {triggerContents}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            role="dialog"
            aria-label="Advanced filters"
            side="right"
            align="start"
            className="max-h-[min(72vh,640px)] w-[360px] overflow-y-auto"
          >
            <AdvancedFilterControls
              filters={filters}
              exchangeOptions={exchangeOptions}
              onFilterPatch={onFilterPatch}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
